import React from 'react';

import { AttentionCircleIcon } from '@vlabs/icons';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';
import { Button } from '@vlabs/uikit/components/controls/button';
import { Input } from '@vlabs/uikit/components/controls/input';
import { useForm } from 'react-hook-form';

import st from './ChangeUserPasswordPage.module.sass';
import { useChangeUserPasswordPageTranslation } from './i18n';
import type { ChangeUserPasswordFormProps, ChangeUserPasswordFormValues } from './types';

const defaultValues: ChangeUserPasswordFormValues = {
  password: '',
  passwordConfirmation: '',
};

const displayName = 'ChangeUserPasswordForm';
const testid = buildTestid(displayName);

function ChangeUserPasswordForm({
  defaultValues: formDefaultValues,
  formError = undefined,
  isLoading,
  isSubmitDisabled,
  onChange,
  onSubmit,
}: ChangeUserPasswordFormProps): React.JSX.Element {
  const { t } = useChangeUserPasswordPageTranslation();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ChangeUserPasswordFormValues>({
    defaultValues: formDefaultValues,
  });

  const password = watch('password');
  const passwordConfirmation = watch('passwordConfirmation');
  const isMismatch = !!password && !!passwordConfirmation && password !== passwordConfirmation;
  let currentError = formError;
  if (isMismatch) currentError = t('error.passwords_mismatch');

  const isDisabled = isSubmitDisabled || isMismatch || !!formError;

  return (
    <form
      className={st.Form}
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className={st.Header}>
        <h5>{t('title')}</h5>

        {!!currentError && (
          <div className={st.Error}>
            <AttentionCircleIcon className={st.ErrorIcon} />
            <span>{currentError}</span>
          </div>
        )}
      </div>

      <div className={st.Input}>
        <Input
          autoFocus
          errors={errors}
          id="password"
          label={t('password.label')}
          type="password"
          {...testid('PasswordInput')}
          {...register('password', {
            required: t('password.required'),
            minLength: 3,
            maxLength: 128,
            onChange,
          })}
        />
      </div>

      <div className={st.Input}>
        <Input
          errors={errors}
          id="passwordConfirmation"
          label={t('password_confirmation.label')}
          type="password"
          {...testid('PasswordConfirmationInput')}
          {...register('passwordConfirmation', {
            required: t('password_confirmation.required'),
            minLength: 3,
            maxLength: 128,
            onChange,
          })}
        />
      </div>

      <div className={st.Actions}>
        <Button
          className={st.Submit}
          disabled={isDisabled}
          isLoading={isLoading}
          type="submit"
          {...testid('SubmitButton')}
        >
          {t('submit')}
        </Button>
      </div>
    </form>
  );
}

export { ChangeUserPasswordForm, defaultValues };
